import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const PostSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().trim().min(1).max(200).regex(/^[a-z0-9-]+$/, "Slug får bara innehålla små bokstäver, siffror och bindestreck"),
  title: z.string().trim().min(1).max(200),
  excerpt: z.string().trim().max(500).optional().or(z.literal("")),
  content: z.string().trim().min(1).max(50000),
  cover_url: z.string().trim().url().max(500).optional().or(z.literal("")),
  published: z.boolean().default(false),
});

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle();
  if (error || !data) throw new Error("Endast admin får göra detta.");
}

export const checkIsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("user_roles").select("role").eq("user_id", context.userId).eq("role", "admin").maybeSingle();
    return { isAdmin: !!data };
  });

export const listAllBlogPostsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await supabaseAdmin
      .from("blog_posts").select("*").order("created_at", { ascending: false });
    if (error) {
      console.error("[blog list]", error);
      throw new Error("Kunde inte hämta inläggen.");
    }
    return { posts: data ?? [] };
  });

export const upsertBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => PostSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const payload = {
      slug: data.slug,
      title: data.title,
      excerpt: data.excerpt || null,
      content: data.content,
      cover_url: data.cover_url || null,
      published: data.published,
      published_at: data.published ? new Date().toISOString() : null,
      author_id: context.userId,
    };
    const { data: row, error } = data.id
      ? await supabaseAdmin.from("blog_posts").update(payload).eq("id", data.id).select().single()
      : await supabaseAdmin.from("blog_posts").insert(payload).select().single();
    if (error) {
      console.error("[blog upsert]", error);
      throw new Error("Kunde inte spara inlägget.");
    }
    return { post: row };
  });

export const deleteBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await supabaseAdmin.from("blog_posts").delete().eq("id", data.id);
    if (error) {
      console.error("[blog delete]", error);
      throw new Error("Kunde inte ta bort inlägget.");
    }
    return { ok: true };
  });
